import {
  localIsoDay,
  perspectiveListSchema,
  perspectivePatchSchema,
  perspectiveSchema,
  perspectiveWeightTotal,
  performanceSettingsSchema,
  periodYearSchema,
  targetSettingsSchema,
  type ModuleKey,
  type Perspective,
} from '@fluxus/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, ShieldCheck } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { AlertSettingsSection } from '@/features/alerts/AlertSettingsSection';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { cn } from '@/lib/cn';
import { type FieldErrors } from '@/lib/forms';
import { PeriodSettingsSection, periodsKey } from '@/pages/PeriodosPage';
import { PerformanceSettingsForm, performanceSettingsKey } from '@/pages/PerformancePage';

const formatWeight = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;

/**
 * Parâmetros da empresa (T-204c, RF-50, RN-03; DEC-070): numa tela só, com as mesmas regras e APIs das telas de cada
 * assunto — semáforo, regra e limite da performance da empresa, justificativa no vermelho, pesos das perspectivas do
 * projeto, aprovação de metas, fechamento automático e alertas. Quem pode administrar edita; os demais veem.
 */
export function ParametrosPage() {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const can = useCan();
  return (
    <PageContainer className="max-w-3xl">
      <PageHeader
        title="Parâmetros da empresa"
        description="As regras que valem para os cálculos e os fluxos desta empresa, num lugar só. Cada parte também aparece na tela do seu assunto."
      />
      <div className="mt-6 flex flex-col gap-8">
        <ParamSection
          title="Performance e semáforo"
          to="configuracoes/performance"
          module="planejamento-estrategico"
          organizationId={organizationId}
        >
          <PerformancePart organizationId={organizationId} />
        </ParamSection>
        <ParamSection
          title="Pesos das perspectivas"
          to="estrategia/perspectivas"
          module="planejamento-estrategico"
          organizationId={organizationId}
        >
          <PerspectiveWeights organizationId={organizationId} />
        </ParamSection>
        <ParamSection
          title="Aprovação de metas"
          to="indicadores/metas"
          module="metas-resultados"
          organizationId={organizationId}
        >
          <TargetApproval organizationId={organizationId} />
        </ParamSection>
        <ParamSection
          title="Fechamento de períodos"
          to="configuracoes/periodos"
          module="metas-resultados"
          organizationId={organizationId}
        >
          <PeriodPart organizationId={organizationId} />
        </ParamSection>
        <ParamSection title="Alertas" to="gestao/alertas" module="alertas" organizationId={organizationId}>
          <AlertSettingsSection
            organizationId={organizationId}
            canEdit={user.organization.isFluxus && can('administracao', 'edit')}
          />
        </ParamSection>
      </div>
    </PageContainer>
  );
}

function ParamSection({
  title,
  to,
  module,
  organizationId,
  children,
}: {
  title: string;
  to: string;
  /** Módulo da tela do assunto: o link só aparece para quem pode abri-la. */
  module: ModuleKey;
  organizationId: string;
  children: ReactNode;
}) {
  const can = useCan();
  return (
    <section aria-label={title} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-bold">{title}</h2>
        {can(module, 'view') ? (
          <Link
            to={`/empresas/${organizationId}/${to}`}
            className="text-sm font-medium text-brand-strong underline"
          >
            Abrir a tela de {title.toLowerCase()}
          </Link>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function PerformancePart({ organizationId }: { organizationId: string }) {
  const { client } = useAuth();
  const settings = useQuery({
    queryKey: performanceSettingsKey(organizationId),
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/performance-settings`, {
        schema: performanceSettingsSchema,
        signal,
      }),
  });
  if (settings.isPending) return <LoadingState rows={3} label="Carregando os parâmetros de performance" />;
  if (settings.isError) {
    return (
      <ErrorState
        error={settings.error}
        onRetry={() => void settings.refetch()}
        retrying={settings.isFetching}
      />
    );
  }
  return (
    <PerformanceSettingsForm
      key={settings.data.updatedAt ?? 'padrao'}
      organizationId={organizationId}
      initial={settings.data}
    />
  );
}

function PeriodPart({ organizationId }: { organizationId: string }) {
  const { client } = useAuth();
  const can = useCan();
  // As regras do fechamento são de Metas e Resultados (a API pede "ver" nesse módulo).
  const allowed = can('metas-resultados', 'view');
  const year = Number(localIsoDay().slice(0, 4));
  // As regras do fechamento vêm junto com os meses do ano (a mesma consulta da tela de períodos).
  const data = useQuery({
    queryKey: periodsKey(organizationId, year),
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/periods`, {
        query: { year },
        schema: periodYearSchema,
        signal,
      }),
    enabled: allowed,
  });
  if (!allowed) {
    return (
      <p className="flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm">
        <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        As regras do fechamento de períodos ficam com quem acessa Metas e Resultados.
      </p>
    );
  }
  if (data.isPending) return <LoadingState rows={2} label="Carregando as regras do fechamento" />;
  if (data.isError) {
    return <ErrorState error={data.error} onRetry={() => void data.refetch()} retrying={data.isFetching} />;
  }
  return (
    <PeriodSettingsSection
      key={data.data.settings.updatedAt ?? 'padrao'}
      organizationId={organizationId}
      initial={data.data.settings}
    />
  );
}

/** Aprovação de metas (DEC-004 item 8): ligar ou desligar, com confirmação, só para o Admin Fluxus. */
function TargetApproval({ organizationId }: { organizationId: string }) {
  const { client } = useAuth();
  const user = useCurrentUser();
  const can = useCan();
  const queryClient = useQueryClient();
  const canConfigure = user.organization.isFluxus && can('administracao', 'edit');
  const [confirming, setConfirming] = useState(false);
  // A mesma chave da tela de metas: mudar aqui já vale lá.
  const key = ['target-settings', organizationId];
  const settings = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/target-settings`, {
        schema: targetSettingsSchema,
        signal,
      }),
  });
  const toggle = useMutation({
    meta: { handlesError: true },
    mutationFn: (next: boolean) =>
      client.request(`/organizations/${organizationId}/target-settings`, {
        method: 'PUT',
        body: { approvalRequired: next },
        schema: targetSettingsSchema,
      }),
    onError: () => setConfirming(false),
    onSuccess: async (result) => {
      setConfirming(false);
      toast.success(result.approvalRequired ? 'Aprovação de metas ligada' : 'Aprovação de metas desligada');
      await queryClient.invalidateQueries({ queryKey: key });
    },
  });
  if (settings.isPending) return <LoadingState rows={1} label="Carregando a regra das metas" />;
  if (settings.isError) {
    return (
      <ErrorState
        error={settings.error}
        onRetry={() => void settings.refetch()}
        retrying={settings.isFetching}
      />
    );
  }
  const on = settings.data.approvalRequired;
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-5">
      <p className="flex items-start gap-2 text-sm">
        <ShieldCheck className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span>
          <span className="font-semibold">Aprovação de metas {on ? 'ligada' : 'desligada'}.</span>{' '}
          <span className="text-muted-foreground">
            {on
              ? 'Meta nova ou revisada fica pendente e só vale depois de aprovada.'
              : 'O que se grava já vale.'}{' '}
            Meta aprovada só é alterada por quem aprova metas, com justificativa.
          </span>
        </span>
      </p>
      {canConfigure ? (
        <div>
          <Button variant="outline" size="sm" onClick={() => setConfirming(true)}>
            {on ? 'Desligar a aprovação' : 'Ligar a aprovação'}
          </Button>
        </div>
      ) : (
        <ReadOnlyNote />
      )}
      {toggle.isError ? <FormAlert tone="error">{errorMessage(toggle.error)}</FormAlert> : null}
      <ConfirmDialog
        open={confirming}
        onOpenChange={(open) => !open && setConfirming(false)}
        title={on ? 'Desligar a aprovação de metas?' : 'Ligar a aprovação de metas?'}
        description={
          on
            ? 'O que se gravar depois já vale. As metas que estão pendentes continuam esperando decisão.'
            : 'Meta nova ou revisada passa a ficar pendente e só vale depois de aprovada por quem aprova metas.'
        }
        confirmLabel={on ? 'Desligar' : 'Ligar'}
        busy={toggle.isPending}
        onConfirm={() => toggle.mutate(!on)}
      />
    </div>
  );
}

function ReadOnlyNote() {
  return (
    <p className="flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm">
      <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      Só o Admin Fluxus altera esta regra.
    </p>
  );
}

/**
 * Pesos das perspectivas no projeto (DEC-004 itens 1 e 3): o peso de cada perspectiva ativa na performance da
 * empresa; a soma deveria dar 100% (senão o cálculo normaliza). Quem edita o planejamento estratégico altera.
 */
function PerspectiveWeights({ organizationId }: { organizationId: string }) {
  const { client } = useAuth();
  const canDo = useCan();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);
  const projectId = project?.id ?? '';
  const base = `/organizations/${organizationId}/projects/${projectId}/perspectives`;
  const list = useQuery({
    queryKey: ['perspectives', projectId, false],
    queryFn: ({ signal }) => client.request(base, { schema: perspectiveListSchema, signal }),
    enabled: Boolean(projectId),
  });
  if (workspace.isPending) return <LoadingState rows={2} label="Carregando o projeto" />;
  if (workspace.isError) {
    return (
      <ErrorState
        error={workspace.error}
        onRetry={() => void workspace.refetch()}
        retrying={workspace.isFetching}
      />
    );
  }
  if (!project) {
    return (
      <EmptyState
        icon={ShieldCheck}
        title="Nenhum projeto BSC"
        description="Os pesos das perspectivas são do projeto. Quando a empresa tiver um projeto, eles aparecem aqui."
      />
    );
  }
  const canEdit = project.status === 'ACTIVE' && canDo('planejamento-estrategico', 'edit');
  return (
    <div className="flex flex-col gap-3">
      <ProjectPicker projects={projects} project={project} onSelect={select} />
      {list.isPending ? (
        <LoadingState rows={2} label="Carregando as perspectivas" />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />
      ) : (
        <WeightsForm
          key={`${projectId}:${list.data.items.map((p) => `${p.id}=${p.weight}`).join(',')}`}
          base={base}
          projectId={projectId}
          perspectives={list.data.items.filter((p) => !p.archivedAt)}
          canEdit={canEdit}
          archived={project.status !== 'ACTIVE'}
        />
      )}
    </div>
  );
}

function WeightsForm({
  base,
  projectId,
  perspectives,
  canEdit,
  archived,
}: {
  base: string;
  projectId: string;
  perspectives: Perspective[];
  canEdit: boolean;
  archived: boolean;
}) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(perspectives.map((p) => [p.id, p.weight === null ? '' : String(p.weight)])),
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const parse = (v: string) => (v.trim() === '' ? null : v.trim().replace(',', '.'));
  // A soma acompanha o que está digitado (como na tela de perspectivas, só as ativas).
  const typed = perspectives.map((p) => {
    const n = Number(parse(values[p.id] ?? ''));
    return { weight: parse(values[p.id] ?? '') === null || !Number.isFinite(n) ? null : n, archivedAt: null };
  });
  const total = perspectiveWeightTotal(typed);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const next: FieldErrors = {};
    const changes: { id: string; weight: number | null }[] = [];
    for (const p of perspectives) {
      const parsed = perspectivePatchSchema.safeParse({ weight: parse(values[p.id] ?? '') });
      if (!parsed.success) {
        next[p.id] = parsed.error.issues[0]?.message;
        continue;
      }
      const weight = parsed.data.weight ?? null;
      if (weight !== p.weight) changes.push({ id: p.id, weight });
    }
    setErrors(next);
    if (Object.values(next).some(Boolean)) return;
    if (!changes.length) {
      toast.info('Nenhum peso mudou');
      return;
    }
    setSaving(true);
    try {
      for (const c of changes) {
        await client.request(`${base}/${c.id}`, {
          method: 'PATCH',
          body: { weight: c.weight },
          schema: perspectiveSchema,
        });
      }
      toast.success('Pesos salvos');
    } catch (err) {
      setFormError(err);
    } finally {
      setSaving(false);
      // Atualiza o que foi salvo (mesmo se uma das perspectivas falhou no meio).
      await queryClient.invalidateQueries({ queryKey: ['perspectives', projectId] });
    }
  }

  if (!perspectives.length) {
    return <p className="text-sm text-muted-foreground">O projeto não tem perspectivas ativas.</p>;
  }
  return (
    <form
      noValidate
      onSubmit={submit}
      aria-label="Pesos das perspectivas"
      className="flex flex-col gap-4 rounded-lg border border-border bg-surface p-5"
    >
      {archived ? (
        <p className="text-sm text-muted-foreground">Projeto arquivado: os pesos ficam só para consulta.</p>
      ) : !canEdit ? (
        <p className="flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm">
          <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          Só quem edita o planejamento estratégico altera os pesos.
        </p>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        {perspectives.map((p) => {
          const id = `peso-${p.id}`;
          return (
            <FormField key={p.id} id={id} label={`Peso de ${p.name} (%)`} error={errors[p.id]}>
              <Input
                {...fieldA11y(id, errors[p.id])}
                value={values[p.id] ?? ''}
                inputMode="decimal"
                maxLength={6}
                placeholder="Sem peso"
                disabled={!canEdit || saving}
                onChange={(e) => {
                  setValues((v) => ({ ...v, [p.id]: e.target.value }));
                  setErrors((x) => ({ ...x, [p.id]: undefined }));
                }}
                className="h-11"
              />
            </FormField>
          );
        })}
      </div>
      <p
        aria-live="polite"
        className={cn(
          'text-sm font-semibold',
          total === 100 ? 'text-status-green-fg' : 'text-status-yellow-fg',
        )}
      >
        Soma: {formatWeight(total)}
        <span className="font-normal">
          {total === 100
            ? ' — tudo certo'
            : ' — o ideal é somar 100%; fora disso, o cálculo normaliza os pesos'}
        </span>
      </p>
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      {canEdit ? (
        <div className="flex justify-end">
          <Button type="submit" loading={saving}>
            Salvar pesos
          </Button>
        </div>
      ) : null}
    </form>
  );
}
